/**
 * Template + fill -> assembled document.
 *
 * Rules (PLAN.md 3.6, 3.6a, 3.16):
 *  - a sentence drops if any top-level placeholder in it is empty, or its `when` fails
 *  - a fragment drops as a unit on the same test; the sentence survives
 *  - a paragraph / list item / heading with no surviving sentence drops
 *  - a table body row drops when a cell that had content ends up empty
 *  - clause sections render selected options in the admin's order, numbered
 *    contiguously counting only clauses that produced text
 *  - party blocks repeat per party using the variant for that party's type
 *  - Tier 3 values sent as presence become holes the browser fills
 *
 * Pure: same input, same output, no I/O. Runs in the Worker and the browser.
 */
import {
  itemsOf, typeLabel,
  type Block, type ContentBlock, type Fill, type InlineRun, type Marks, type ParagraphBlock, type Sentence, type Template,
} from '@saledeed/shared';

type TableRow = Extract<ContentBlock, { type: 'table' }>['rows'][number];
import {
  conditionHolds, defaultNameFields, fieldMap, joinNames, nameFieldFor, resolveField, selectedOptions,
  type Ctx, type PartyCtx,
} from './context';
import { isBlank, normalizeRuns } from './normalize';
import type { AssembledDocument, AssemblyWarning, OutBlock, OutRun, ParagraphRole } from './output';

const NO_MARKS: Marks = {};

interface State { warnings: AssemblyWarning[]; template: Template }

function holeRun(ctx: Ctx, field: string, marks: Marks): OutRun {
  const def = ctx.fields.get(field);
  if (def?.scope === 'party' && ctx.party) {
    return { kind: 'hole', field, party: { side: ctx.party.side, index: ctx.party.index }, marks };
  }
  return { kind: 'hole', field, marks };
}

/** Returns false when the run needs a value it does not have (caller drops). */
function inline(ctx: Ctx, st: State, run: InlineRun, path: string, out: OutRun[]): boolean {
  const marks = run.marks ?? NO_MARKS;
  switch (run.kind) {
    case 'text':
      out.push({ kind: 'text', text: run.text, marks });
      return true;

    case 'placeholder': {
      const r = resolveField(ctx, run.field, run.format ?? 'default');
      if (r.kind === 'empty') return false;
      out.push(r.kind === 'hole' ? holeRun(ctx, run.field, marks) : { kind: 'text', text: r.text, marks });
      return true;
    }

    case 'plural': {
      const count = itemsOf(ctx.fill, run.side).length;
      out.push({ kind: 'text', text: count === 1 ? run.one : run.many, marks });
      return true;
    }

    case 'partyRef': {
      const parties = itemsOf(ctx.fill, run.side);
      const names: string[] = [];
      parties.forEach((data, index) => {
        const fid = nameFieldFor(ctx, run.side, data.type, run.fields);
        if (!fid) return;
        const r = resolveField({ ...ctx, party: { side: run.side, index, data } }, fid);
        if (r.kind === 'text') names.push(r.text);
      });
      if (names.length === 0) return false;
      if (names.length < parties.length) {
        st.warnings.push({
          code: 'partyRef.incomplete',
          path,
          message: run.side === 'property'
            ? 'Some properties are not filled in yet, so they are missing from a list of properties'
            : `Some ${run.side}s have no name yet, so they are missing from a list of ${run.side}s`,
        });
      }
      out.push({ kind: 'text', text: joinNames(names), marks });
      return true;
    }
  }
}

function sentence(ctx: Ctx, st: State, s: Sentence, path: string): OutRun[] | null {
  if (s.when && !conditionHolds(ctx, s.when)) return null;
  const out: OutRun[] = [];
  for (const run of s.runs) {
    if (run.kind === 'fragment') {
      if (run.when && !conditionHolds(ctx, run.when)) continue;
      const tmp: OutRun[] = [];
      if (run.runs.every(r => inline(ctx, st, r, path, tmp))) out.push(...tmp);
      continue;
    }
    if (!inline(ctx, st, run, path, out)) return null;
  }
  return out;
}

/** Surviving sentences joined with a space and normalised; null if nothing survives. */
function sentences(ctx: Ctx, st: State, list: readonly Sentence[], path: string): OutRun[] | null {
  const parts: OutRun[] = [];
  list.forEach((s, i) => {
    const r = sentence(ctx, st, s, `${path}.sentences[${i}]`);
    if (!r) return;
    if (parts.length) parts.push({ kind: 'text', text: ' ', marks: NO_MARKS });
    parts.push(...r);
  });
  if (!parts.length) return null;
  const norm = normalizeRuns(parts);
  return isBlank(norm) ? null : norm;
}

function paragraph(ctx: Ctx, st: State, b: ParagraphBlock, path: string, role: ParagraphRole, idSuffix: string): OutBlock | null {
  const runs = sentences(ctx, st, b.sentences, path);
  return runs ? { type: 'paragraph', id: b.id + idSuffix, role, runs, ...(b.spacing ? { spacing: b.spacing } : {}) } : null;
}

function content(ctx: Ctx, st: State, b: ContentBlock, path: string, role: ParagraphRole, idSuffix: string): OutBlock | null {
  switch (b.type) {
    case 'heading': {
      const runs = sentences(ctx, st, [b.sentence], path);
      return runs ? { type: 'heading', id: b.id + idSuffix, level: b.level, runs, ...(b.spacing ? { spacing: b.spacing } : {}) } : null;
    }
    case 'paragraph':
      return paragraph(ctx, st, b, path, role, idSuffix);
    case 'list': {
      const items = b.items
        .map((it, i) => sentences(ctx, st, it.sentences, `${path}.items[${i}]`))
        .filter((r): r is OutRun[] => r !== null);
      return items.length ? { type: 'list', id: b.id + idSuffix, ordered: b.ordered, items } : null;
    }
    case 'table': {
      const rows: OutRun[][][] = [];
      let bodyRows = 0;
      const one = (rctx: Ctx, row: TableRow, ri: number, isHeader: boolean) => {
        let emptied = false;
        const cells = row.cells.map((cell, ci) => {
          if (cell.sentences.length === 0) return []; // deliberately blank cell
          const r = sentences(rctx, st, cell.sentences, `${path}.rows[${ri}].cells[${ci}]`);
          if (!r) emptied = true;
          return r ?? [];
        });
        if (!isHeader && emptied) return;
        rows.push(cells);
        if (!isHeader) bodyRows++;
      };
      b.rows.forEach((row, ri) => {
        const isHeader = b.headerRow && ri === 0;
        // A repeated row prints once per seller / buyer / property, with that item's values.
        if (row.repeat && !isHeader) {
          const side = row.repeat;
          itemsOf(ctx.fill, side).forEach((data, index) => one({ ...ctx, party: { side, index, data } }, row, ri, false));
          return;
        }
        one(ctx, row, ri, isHeader);
      });
      return bodyRows ? { type: 'table', id: b.id + idSuffix, headerRow: b.headerRow, rows } : null;
    }
  }
}

function block(ctx: Ctx, st: State, b: Block, path: string, out: OutBlock[]): void {
  switch (b.type) {
    case 'heading':
    case 'paragraph':
    case 'list':
    case 'table': {
      const r = content(ctx, st, b, path, 'body', '');
      if (r) out.push(r);
      return;
    }

    case 'clauseSection': {
      let n = 1;
      b.entries.forEach((entry, ei) => {
        const epath = `${path}.entries[${ei}]`;
        const chosen = selectedOptions(entry, ctx.fill);
        let produced = 0;
        for (const opt of chosen) {
          const oi = entry.options.indexOf(opt);
          const paras = opt.body
            .map((p, pi) => paragraph(ctx, st, p, `${epath}.options[${oi}].body[${pi}]`, 'clause', ''))
            .filter((p): p is Extract<OutBlock, { type: 'paragraph' }> => p !== null);
          if (!paras.length) continue;
          produced++;
          paras[0] = { ...paras[0]!, number: `${n}.` };
          n++;
          out.push(...paras);
        }
        if (chosen.length > 0 && produced === 0) {
          st.warnings.push(entry.min >= 1
            ? { code: 'clause.requiredEmpty', path: epath, message: `Required clause "${entry.label}" is empty and will be missing from the document` }
            : { code: 'clause.selectedEmpty', path: epath, message: `Clause "${entry.label}" was chosen but is empty, so it is left out` });
        }
      });
      return;
    }

    case 'partyBlock': {
      const parties = itemsOf(ctx.fill, b.side);
      if (!parties.length) {
        st.warnings.push({ code: 'party.none', path, message: b.side === 'property' ? 'No property has been added' : `No ${b.side} has been added` });
        return;
      }
      parties.forEach((data, index) => {
        const variant = b.variants[data.type];
        if (!variant) {
          st.warnings.push({
            code: 'party.variantMissing',
            path: `${path}.variants.${data.type}`,
            message: `This template has no wording for a ${typeLabel(st.template, data.type)} ${b.side}`,
          });
          return;
        }
        const party: PartyCtx = { side: b.side, index, data };
        const pctx: Ctx = { ...ctx, party };
        const suffix = `@${b.side}.${index}`;
        let numbered = false;
        variant.forEach((cb, ci) => {
          const r = content(pctx, st, cb, `${path}.variants.${data.type}[${ci}]`, 'party', suffix);
          if (!r) return;
          if (b.numbered && !numbered && r.type === 'paragraph') {
            out.push({ ...r, number: `${index + 1}.` });
            numbered = true;
          } else {
            out.push(r);
          }
        });
      });
      return;
    }
  }
}

export function assemble(template: Template, fill: Fill): AssembledDocument {
  const ctx: Ctx = { fields: fieldMap(template), fill, party: null, names: defaultNameFields(template) };
  const st: State = { warnings: [], template };
  const blocks: OutBlock[] = [];
  template.blocks.forEach((b, i) => block(ctx, st, b, `blocks[${i}]`, blocks));
  return { title: template.title, blocks, warnings: st.warnings };
}
